import React from 'react';
import { Helmet } from 'react-helmet';
import { Navigate, type RouteObject } from 'react-router-dom';
import Login from '../views/Login';
import RouteGuard from './RouteGuard';
import NoMatch from '../views/NoMatch';
import Account from '../views/Account';
import AppLayout from '../components/layout/AppLayout';
import Dashboard from '../views/Dashboard';
import DatasourcesList from '../views/datasources/DatasourcesList';
import DatasourceEdit from '../views/datasources/DatasourceEdit';
import DatasourceDetail from '../views/datasources/DatasourceDetail';
import DatasetsList from '../views/datasets/DatasetsList';
import DatasetEdit from '../views/datasets/DatasetEdit';
import DatasetDetail from '../views/datasets/DatasetDetail';
import ModelsList from '../views/bda-models/ModelsList';
import ModelDetail from '../views/bda-models/ModelDetail';
import ServicesList from '../views/bda-services/ServicesList';
import ServiceEdit from '../views/bda-services/ServiceEdit';
import ServiceDetail from '../views/bda-services/ServiceDetail';
import BdaApplicationsList from '../views/bda-applications/BdaApplicationsList';
import BdaApplicationEdit from '../views/bda-applications/BdaApplicationEdit';
import BdaApplicationDetail from '../views/bda-applications/BdaApplicationDetail';
import ProjectsList from '../views/projects/ProjectsList';
import ProjectDetail from '../views/projects/ProjectDetail';
import ProjectEdit from '../views/projects/ProjectEdit';
import NotificationsList from '../views/notifications/NotificationsList';
import NotificationPreferences from '../views/notifications/NotificationPreferences';
import ControlPanelHome from '../views/control-panel/ControlPanelHome';
import UICustomization from '../views/control-panel/UICustomization';
import UsersList from '../views/control-panel/users-management/UsersList';
import UserEdit from '../views/control-panel/users-management/UserEdit';
import UserDetail from '../views/control-panel/users-management/UserDetail';
import OrganizationsList from '../views/control-panel/organizations-management/OrganizationsList';
import OrganizationMembers from '../views/control-panel/organizations-management/OrganizationMembers';
import RagsList from '../views/llm/rag/RagsList';
import RagChat from '../views/llm/rag/RagChat';
import LoadersList from '../views/llm/loaders/LoadersList';
import LlmModelsList from '../views/llm/models/ModelsList';
import DataspaceConnectorsList from '../views/dataspace-connectors/DataspaceConnectorsList';
import DataspaceConnectorDetail from '../views/dataspace-connectors/DataspaceConnectorDetail';
import ModelEdit from '../views/bda-models/ModelEdit';
import PermissionsList from '../views/control-panel/permissions-management/PermissionsList';
import PublishResource from '../views/publish-resource/PublishResource';
import ApiKeysList from '../views/api-keys/ApiKeysList';
import RedisVariablesList from '../views/control-panel/redis-variables-management/RedisVariablesList';
import EnvironmentVariablesList from '../views/environment-settings/EnvironmentVariablesList';
import EnvironmentVariableDetail from '../views/environment-settings/EnvironmentVariableDetail';
import CategoriesManagement from '../views/control-panel/CategoriesManagement';
import SectionsList from '../views/control-panel/sections-management/SectionsList';
import EnvCustomization from '../views/control-panel/EnvCustomization';
import ClusterDetail from '../views/control-panel/multi-cluster-management/ClusterDetail';
import ClusterNodeDetail from '../views/control-panel/multi-cluster-management/ClusterNodeDetail';
import TargetsList from '../views/control-panel/targets-management/TargetsList';
import NotesList from '../views/control-panel/multi-cluster-management/NotesList';
import ServicePropertiesEdit from '../views/bda-services/ServicePropertiesEdit';
import LoaderEdit from '../views/llm/loaders/LoaderEdit';

type Props = {
  title: string;
  element: React.ReactNode;
}

function Page({ title, element }: Props) {
  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      {element}
    </>
  )
}

export const APP_ROUTES: Array<RouteObject> = [
  {
    path: '/',
    element: <AppLayout />,
    children: [
      { index: true, element: <Navigate to={window.__env.AFTER_LOGIN_ROUTE || "/dashboard"} /> },
      {
        path: '/dashboard',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="Dashboard" element={<Dashboard />} /> },
        ],
      },
      {
        path: '/account',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="Account" element={<Account />} /> },
        ],
      },
      {
        path: '/environment-variables',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="Global Variables" element={<EnvironmentVariablesList />} /> },
          { path: ':id', element: <EnvironmentVariableDetail /> },
        ],
      },
      {
        path: '/datasources',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="Datasources List" element={<DatasourcesList />} /> },
          { path: 'create', element: <DatasourceEdit /> },
          { path: ':id', element: <DatasourceDetail /> },
          { path: ':id/edit', element: <DatasourceEdit /> },
        ]
      },
      {
        path: '/datasets',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="Datasets List" element={<DatasetsList />} /> },
          { path: 'create', element: <DatasetEdit /> },
          { path: ':id', element: <DatasetDetail /> },
          { path: ':id/edit', element: <DatasetEdit /> },
          { path: ':id/publish', element: <PublishResource resourceType="dataset" /> },
        ]
      },
      {
        path: '/models',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="Models List" element={<ModelsList />} /> },
          { path: 'create', element: <ModelEdit /> },
          { path: ':id', element: <ModelDetail /> },
          { path: ':id/publish', element: <PublishResource resourceType="model" /> },
          { path: ':id/edit', element: <ModelEdit /> },
        ]
      },
      {
        path: '/dataspace-connectors',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="Data Space Connectors List" element={<DataspaceConnectorsList />} /> },
          { path: ':id', element: <DataspaceConnectorDetail /> },
        ]
      },
      {
        path: '/services',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="Services List" element={<ServicesList />} /> },
          { path: 'create', element: <ServiceEdit /> },
          { path: ':id', element: <ServiceDetail /> },
          { path: ':id/edit', element: <ServiceEdit /> },
        ]
      },
      {
        path: '/bda-applications',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="Applications List" element={<BdaApplicationsList />} /> },
          { path: 'create', element: <BdaApplicationEdit /> },
          { path: ':id', element: <BdaApplicationDetail /> },
          { path: ':id/edit', element: <BdaApplicationEdit /> },
          { path: ':id/services/:serviceId', element: <ServicePropertiesEdit /> },
        ]
      },
      {
        path: '/projects',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="Projects List" element={<ProjectsList />} /> },
          { path: 'create', element: <ProjectEdit /> },
          { path: ':id', element: <ProjectDetail /> },
          { path: ':id/edit', element: <ProjectEdit /> },
        ]
      },
      {
        path: '/notifications',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="Notifications" element={<NotificationsList />} /> },
          { path: 'preferences', element: <Page title="Notification Preferences" element={<NotificationPreferences />} /> },
        ]
      },
      {
        path: '/api-keys',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Page title="API Keys List" element={<ApiKeysList />} /> },
        ]
      },
      {
        path: '/llm',
        element: <RouteGuard />,
        children: [
          { index: true, element: <Navigate to="rags" /> },
          {
            path: 'rags',
            children: [
              { index: true, element: <Page title="Rags List" element={<RagsList />} /> },
              { path: ":id", element: <RagChat /> },
            ]
          },
          {
            path: 'loaders',
            children: [
              { index: true, element: <Page title="Loaders List" element={<LoadersList />} /> },
              { path: 'create', element: <LoaderEdit /> },
              { path: ':id/edit', element: <LoaderEdit /> },
            ]
          },
          {
            path: 'models',
            children: [
              { index: true, element: <Page title="Models List" element={<LlmModelsList />} /> },
            ]
          }
        ]
      },
      {
        path: '/control-panel',
        element: <RouteGuard checkAdmin={true} />,
        children: [
          { index: true, element: <Page title="Control Panel" element={<ControlPanelHome />} /> },
          {
            path: 'users-management',
            children: [
              { index: true, element: <Page title="Control Panel - Users Management" element={<UsersList />} /> },
              { path: 'create', element: <UserEdit /> },
              { path: ':id', element: <UserDetail /> },
              { path: ':id/edit', element: <UserEdit /> },
            ]
          },
          {
            path: 'cluster-management',
            children: [
              { index: true, element: <Page title="Control Panel - Cluster Management" element={<ClusterDetail />} /> },
              // { path: ':id', element: <ClusterDetail />, },
              // { path: ':id/nodes/:nodeId', element: <ClusterNodeDetail /> },
              // { path: ':id/notes', element: <NotesList /> },
              { path: 'notes', element: <NotesList /> },
              { path: 'nodes/:nodeId', element: <ClusterNodeDetail /> }
            ]
          },
          {
            path: 'organizations-management',
            children: [
              { index: true, element: <Page title="Control Panel - Organizations Management" element={<OrganizationsList />} /> },
              { path: ':id/manage-members', element: <OrganizationMembers /> },
            ]
          },
          {
            path: 'targets-management',
            children: [
              { index: true, element: <Page title="Control Panel - Targets Management" element={<TargetsList />} /> },
            ]
          },
          {
            path: 'categories-management',
            children: [
              { index: true, element: <Page title="Control Panel - Categories Management" element={<CategoriesManagement />} /> },
            ]
          },
          {
            path: 'permissions-management',
            children: [
              { index: true, element: <Page title="Control Panel - Permissions Management" element={<PermissionsList />} /> },
            ]
          },
          {
            path: 'categories-management',
            children: [
              { index: true, element: <Page title="Control Panel - Categories Management" element={<CategoriesManagement />} /> },
            ]
          },
          {
            path: 'sections-management',
            children: [
              { index: true, element: <Page title="Control Panel - Sections Management" element={<SectionsList />} /> },
            ]
          },
          {
            path: 'env-customization',
            children: [
              { index: true, element: <Page title="Control Panel - Environment Customization" element={<EnvCustomization />} /> },
            ]
          },
          {
            path: 'ui-customization',
            children: [
              { index: true, element: <Page title="Control Panel - User Interface Customization" element={<UICustomization />} /> },
            ]
          },
          {
            path: 'redis-variables-management',
            children: [
              { index: true, element: <Page title="Control Panel - Redis Variables Management" element={<RedisVariablesList />} /> },
            ]
          },
        ]
      },
      { path: '*', element: <NoMatch /> },
      { path: '/login', element: <Page title="Login" element={<Login />} /> },
    ]
  },
]